import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import { Button } from "devextreme-react/button";
import {
    DataGrid,
    Column,
    Editing,
    FilterRow,
    Paging,
    Pager,
    Sorting,
    Scrolling,
    LoadPanel,
    Selection,
    ColumnChooser,
    ColumnChooserSearch,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
} from "devextreme-react/data-grid";
import { createStore } from "devextreme-aspnet-data-nojquery";
import DataSource from "devextreme/data/data_source";
import notify from "devextreme/ui/notify";
import { alert, confirm } from "devextreme/ui/dialog";
import axios from "axios";
import routes from "../../../../../assets/apiRoutes.json";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import { buildDxFilters } from "../../../../../common/Utils";
import { MESSAGES } from "../../../../../common/Messages";
import { SelectItemsSearchLayout } from "./SelectItemsLayout";

const emptyGuid = "00000000-0000-0000-0000-000000000000";
const allowedPageSizes = [10, 20, 50, 100];

// IMITEM.ITEMTYPE - only a lot-controlled item has Lot Net Available to check against.
const ITEM_TYPE_LOT = "L";
// OMTRANSACTIONDP.ITEMLOTNETAVAILWARNING, mirroring Argos.Core.Constants.ItemLotNetAvailWarning.
// Legacy tests "<> 'N'" for whether the check runs and "= 'S'" for whether it blocks, so any other
// code - including a blank one - warns and lets the user through.
const NET_AVAIL_WARNING_NONE = "N";
const NET_AVAIL_WARNING_BLOCK = "S";
// OMTRANSACTIONDP.STATUSCHECK_* for the order's stage, mirroring Argos.Core.Constants.QuantityStatusCheckFlags:
// 'A'/'C' warn when Ordered exceeds Available, 'B'/'D' refuse it, and any other code runs no check.
const STATUS_CHECK_WARN_CODES = ["A", "C"];
const STATUS_CHECK_BLOCK_CODES = ["B", "D"];

// The settings the picker falls back to until GetSelectItemsSettings answers, and whenever it fails:
// the narrowest reading - no lot columns, no price editing, no availability check - so nothing extra
// is offered in the window before the profile is known.
const DEFAULT_SELECT_ITEMS_SETTINGS = {
    manualLotSelection: false,
    allowPriceEdit: false,
    netAvailableWarning: NET_AVAIL_WARNING_NONE,
    customerCrossReferenceMode: false,
    quantityStatusCheck: null,
};

// uOMD0027.UpdatePrice / CalculatePrices: Disc Price is always Price less Disc %. Null while there is no price.
const discPriceOf = (price, discPercent) => {
    const p = Number(price);
    if (price == null || price === "" || !Number.isFinite(p)) return null;
    const d = Number(discPercent);
    return p - (p * (Number.isFinite(d) ? d : 0)) / 100;
};

/**
 * OM Sales Order "Select Items" popup — React replacement for the legacy Delphi OMD0027.
 * Filters (FormField) at the top, a server-side-paged grid of items with availability below.
 * The user types an Ordered quantity (and optionally overrides Unit Price) on one or more
 * rows, then "Add Selected Items" appends them as new detail lines to the current order.
 * Entered quantities survive paging because they live in the grid's batch-edit `changes`.
 *
 * The order's OM Transaction Data Profile decides how the grid renders, the same flags the legacy form
 * reads in CustomCreate/FormAbLoadProgram/qryItemsBeforeOpen:
 *  - LOTMETHOD_SHIPMENTS = 'M' returns one row per item/lot and sends the lot the user picked; every
 *    other code returns one row per item and lets the server allocate lots per line. The four Lot
 *    columns are on the grid in both modes (read-only, empty in automatic mode) — unlike legacy,
 *    which hid them; see the columns below.
 *  - ACCESSUNITPRICE = 'Y' makes Price and Disc Price editable.
 *  - STATUSCHECK_<stage> warns, or refuses, when Ordered exceeds Available ('C'/'D' also restrict the
 *    list to a positive Available, which the server applies).
 *  - ITEMLOTNETAVAILWARNING warns, or refuses, when Ordered exceeds Net Available (SR-048727).
 * All of them are re-read server-side when the selection is appended — what is done here is for the
 * user's benefit, not a security boundary.
 *
 * Price follows Ordered (legacy re-prices on every change, so quantity breaks show) unless the user typed
 * the row's price, and Disc Price is always Price less Disc % — editing it sets the Disc % it implies.
 */
function SelectItemsPopup(props) {
    const { showPopup = {}, setShowPopup, onSuccess, setLoadPanelVisible, oidc } = props;

    const visible = showPopup.visible === true;
    const headerRowId = showPopup.headerRowId;
    const warehouseId = showPopup.warehouseId;
    // Pricing context taken from the already-loaded order header (passed in via showPopup) so the backend
    // doesn't re-read the header. Sent with OmTransOrderDetailDto field names; the backend resolves the price
    // date from stage + ship/transaction dates.
    const customerId = showPopup.customerId || "";
    const consigneeId = showPopup.consigneeId || "";
    const priceLevel = showPopup.priceLevel || "";
    const stage = showPopup.stage || "";
    const toIsoDate = (v) => {
        if (!v) return "";
        const d = new Date(v);
        return isNaN(d.getTime()) ? "" : d.toISOString();
    };
    const shipDate = toIsoDate(showPopup.shippingDate);
    const transactionDate = toIsoDate(showPopup.transactionDate);

    // Range/macro selection handed over by the Range dialog (CommonReport) on the Details tab. When one is
    // active the rows come from GetItemsForSelection, which appends these criteria to the item query's WHERE
    // clause in SQL — the point being that a site with far more items than can be browsed is narrowed by the
    // database, not by the grid. With no range the popup keeps calling GetItems exactly as before.
    const rangeFilters = Array.isArray(showPopup.rangeFilters) ? showPopup.rangeFilters : [];
    const hasRange = rangeFilters.length > 0;
    const macroName = showPopup.macroName || "";
    const rangeFiltersJson = useMemo(() => (hasRange ? JSON.stringify(rangeFilters) : null), [hasRange, rangeFilters]);

    const accessToken = oidc?.user?.access_token;
    const currentUser = oidc?.user;
    const DYNAMICFORMSETTING = oidc?.user?.profile?.DYNAMICFORMSETTING;

    const dataGridRef = useRef(null);
    const formRefs = useRef({});

    // Every item filter starts empty — the Warehouse filter is deliberately NOT defaulted to the detail
    // line's warehouse, so the picker shows items from all warehouses. The line's warehouse is still what
    // the created rows are stamped with (see WarehouseID below). Key must match SelectItemsLayout.
    const initialFilters = useMemo(() => ({ WarehouseName: null }), []);

    const [formFields, setformFields] = useState(initialFilters);
    const [resultData, setResultData] = useState(null);
    // Controlled batch-edit changes — the authoritative list of every row whose Ordered/Price/Disc % was edited.
    const [changes, setChanges] = useState([]);
    // Row selection + rows the user removed from the grid (client-side only — never deleted from the DB).
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [removedKeys, setRemovedKeys] = useState([]);

    // Profile settings for this order, defaulted until GetSelectItemsSettings answers.
    const [settings, setSettings] = useState(DEFAULT_SELECT_ITEMS_SETTINGS);

    const manualLot = settings.manualLotSelection === true;
    const netAvailWarning = (settings.netAvailableWarning ?? "").trim();
    const netAvailChecked = netAvailWarning !== NET_AVAIL_WARNING_NONE;
    const netAvailBlocks = netAvailWarning === NET_AVAIL_WARNING_BLOCK;
    const statusCheck = (settings.quantityStatusCheck ?? "").trim().toUpperCase();
    const statusCheckWarns = STATUS_CHECK_WARN_CODES.includes(statusCheck);
    const statusCheckBlocks = STATUS_CHECK_BLOCK_CODES.includes(statusCheck);

    // Keys of the rows whose Price the user typed. Those keep their price when Ordered changes, as the
    // append keeps a price that was sent; every other row is re-priced at its new quantity.
    const userPricedKeysRef = useRef(new Set());

    // Manual-lot mode needs the lot join, which only the raw-SQL picker has, so it uses that endpoint even
    // with no range set. Its rows are keyed item+lot, because one item then contributes a row per lot.
    const useSelectionEndpoint = hasRange || manualLot;
    const rowKeyField = useSelectionEndpoint ? "SelectionKey" : "RowId";

    useEffect(() => {
        if (!visible || !headerRowId || headerRowId === emptyGuid) return undefined;
        let cancelled = false;
        // Drop the previous order's profile before asking for this one's — reopening the picker on a
        // different header must not render against settings the last header resolved.
        setSettings(DEFAULT_SELECT_ITEMS_SETTINGS);
        (async () => {
            try {
                const res = await axios.get(
                    `${window._env_.REACT_APP_APIS}/${routes.OM.GetSelectItemsSettings}`,
                    {
                        params: { HeaderRowId: headerRowId },
                        headers: { access_token: accessToken, Authorization: `Bearer ${accessToken}` },
                    }
                );
                // The endpoint answers 200 with { Success: false } on a failure, so the shape has to be
                // checked rather than the status — otherwise the defaults below read off an error body.
                if (cancelled || !res.data || res.data.Success === false) return;
                setSettings({
                    manualLotSelection: res.data.ManualLotSelection === true,
                    allowPriceEdit: res.data.AllowPriceEdit === true,
                    // Kept raw: legacy treats a blank code as "check, and let the user through", which is
                    // not the same as 'N'. Only a missing field falls back to the no-check default.
                    netAvailableWarning:
                        typeof res.data.NetAvailableWarning === "string"
                            ? res.data.NetAvailableWarning
                            : NET_AVAIL_WARNING_NONE,
                    customerCrossReferenceMode: res.data.CustomerCrossReferenceMode === true,
                    quantityStatusCheck:
                        typeof res.data.QuantityStatusCheck === "string" ? res.data.QuantityStatusCheck : null,
                });
            } catch (err) {
                // Advisory only — the append re-reads every one of these — so a failure here leaves the
                // picker usable on the restrictive defaults rather than blocking it.
                notify({ message: err?.message || MESSAGES.FETCH_ERROR, width: 400 }, "error", 6000);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [visible, headerRowId, accessToken]);

    // Server-side store; the dynamic filters are applied on the DataSource (DevExtreme sends them as loadOptions.filter).
    const buildDataSource = useCallback(() => {
        const store = createStore({
            key: rowKeyField,
            loadUrl: `${window._env_.REACT_APP_APIS}/${useSelectionEndpoint ? routes.IM.GetItemsForSelection : routes.IM.GetItems}`,
            loadParams: {
                // OmTransOrderDetailDto field names — bound on the server as the pricing context.
                transactionHeaderId: headerRowId || "",
                identityMasterRowId: customerId,
                consigneeRowId: consigneeId,
                priceLevel,
                stage,
                shipDate,
                transactionDate,
                // ItemSelectionFilter — ignored by GetItems, so they are only sent when a range is active.
                ...(hasRange ? { RangeFilters: rangeFiltersJson, MacroName: macroName } : {}),
            },
            onBeforeSend(method, ajaxOptions) {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
            // A range the query cannot apply (a field the item view does not carry) or one matching more rows
            // than the server will return comes back as a message the user can act on — surfaced here rather
            // than only in the grid's error row.
            errorHandler: (error) => {
                notify({ message: error?.message || MESSAGES.FETCH_ERROR, width: 400 }, "error", 6000);
            },
        });
        // Disc Price is derived here, once per loaded row, from the Price and Disc % the server returned
        // (the price is the one FNIM_GETPRICE call per row, so the query does not repeat it for this).
        return new DataSource({
            store,
            map: (row) => ({ ...row, DiscPrice: discPriceOf(row.Price, row.DiscPercent) }),
        });
    }, [accessToken, headerRowId, customerId, consigneeId, priceLevel, stage, shipDate, transactionDate, hasRange, rangeFiltersJson, macroName, useSelectionEndpoint, rowKeyField]);

    // Build the dynamic DevExtreme filter from the FormField values (same helper List.js uses) and apply it,
    // AND exclude any rows the user removed from the grid (client-side delete — these never hit the DB).
    const loadWithFilters = useCallback((filters, excluded = []) => {
        const ds = buildDataSource();
        const parts = [];
        const dxFilters = buildDxFilters(filters, SelectItemsSearchLayout);
        if (dxFilters && dxFilters.length) parts.push(dxFilters);
        if (excluded && excluded.length) {
            const conds = excluded.map((k) => [rowKeyField, "<>", k]);
            let exclusion = conds[0];
            if (conds.length > 1) {
                exclusion = [];
                conds.forEach((c, i) => {
                    if (i) exclusion.push("and");
                    exclusion.push(c);
                });
            }
            parts.push(exclusion);
        }
        const combined = parts.length === 2 ? [parts[0], "and", parts[1]] : parts[0] || null;
        if (combined) ds.filter(combined);
        setResultData(ds);
    }, [buildDataSource, rowKeyField]);

    // Reset filters + reload whenever the popup is opened.
    useEffect(() => {
        if (!visible) return;
        setformFields(initialFilters);
        setChanges([]);
        setSelectedRowKeys([]);
        setRemovedKeys([]);
        userPricedKeysRef.current.clear();
        loadWithFilters(initialFilters, []);
    }, [visible, initialFilters, loadWithFilters]);

    const handleFieldChange = useCallback(() => {}, []);
    const handleFormLoad = useCallback(() => {}, []);

    const handleSearch = useCallback(
        (e) => {
            e.event.stopPropagation();
            const grid = dataGridRef.current?.instance();
            if (grid) grid.cancelEditData(); // drop any unsaved quantities from the previous result set
            setChanges([]);
            setSelectedRowKeys([]);
            setRemovedKeys([]);
            userPricedKeysRef.current.clear();
            loadWithFilters(formFields, []);
        },
        [formFields, loadWithFilters]
    );

    const handleReset = useCallback(
        (e) => {
            e.event.stopPropagation();
            const grid = dataGridRef.current?.instance();
            if (grid) grid.cancelEditData();
            setformFields(initialFilters);
            setChanges([]);
            setSelectedRowKeys([]);
            setRemovedKeys([]);
            userPricedKeysRef.current.clear();
            loadWithFilters(initialFilters, []);
        },
        [initialFilters, loadWithFilters]
    );

    // Delete the selected rows from the grid only (client-side) — excluded from the view, never deleted from the DB.
    const handleDeleteRows = useCallback(() => {
        if (!selectedRowKeys.length) return;
        const keys = selectedRowKeys;
        const next = removedKeys.concat(keys.filter((k) => !removedKeys.includes(k)));
        setRemovedKeys(next);
        // Drop any entered quantities for the removed rows so they aren't sent on "Add Selected Items".
        setChanges((prev) => prev.filter((c) => !keys.includes(c.key)));
        keys.forEach((k) => userPricedKeysRef.current.delete(k));
        setSelectedRowKeys([]);
        loadWithFilters(formFields, next);
    }, [selectedRowKeys, removedKeys, formFields, loadWithFilters]);

    // uOMD0027.UpdatePrice for one row: its price at `quantity`, priced by the server exactly as the append
    // prices a row whose Price was left alone. Undefined when it could not be fetched - the row then keeps
    // the price it shows, and the append re-prices it anyway because no Price was typed.
    const fetchRowPrice = useCallback(
        async (itemId, quantity) => {
            if (!itemId || !headerRowId || headerRowId === emptyGuid) return undefined;
            try {
                const res = await axios.get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetSelectItemsPrice}`, {
                    params: { HeaderRowId: headerRowId, ItemRowId: itemId, Quantity: quantity },
                    headers: { access_token: accessToken, Authorization: `Bearer ${accessToken}` },
                });
                if (!res.data || res.data.Success === false) return undefined;
                // Legacy stores 0 when FNIM_GETPRICE has no price for the item.
                return res.data.Price ?? 0;
            } catch (err) {
                notify({ message: err?.message || MESSAGES.FETCH_ERROR, width: 400 }, "error", 6000);
                return undefined;
            }
        },
        [headerRowId, accessToken]
    );

    // The two checks the legacy OnValidate runs, in its order:
    //  1. Quantity Status Check By Stage - Ordered against the item's Available.
    //  2. SR-048727 - Item Net Available on every row, Lot Net Available only in manual-lot mode and only
    //     for lot-type items.
    // A blocking code refuses the quantity outright; a warning asks and leaves the cell unchanged on "no".
    // The server repeats both blocking checks on append, so this is the courtesy, not the control. Once
    // the quantity stands, the row is re-priced at it (cdsItemsLotsOrderedChange) unless its Price was typed.
    //
    // This hangs off the Ordered column's setCellValue rather than onRowUpdating: the grid edits in batch
    // mode and never calls saveEditData - it posts the accumulated changes itself - so onRowUpdating would
    // never fire. Defining setCellValue means the value is only written when this assigns it.
    const setOrderedCellValue = useCallback(
        (newData, value, currentRowData) => {
            const ordered = Number(value);

            const applyValue = async () => {
                newData.Ordered = value;
                const key = currentRowData?.[rowKeyField];
                if (key != null && userPricedKeysRef.current.has(key)) return;
                const price = await fetchRowPrice(currentRowData?.RowId, Number.isFinite(ordered) ? ordered : 0);
                if (price !== undefined) {
                    newData.Price = price;
                    newData.DiscPrice = discPriceOf(price, currentRowData?.DiscPercent);
                }
            };

            if (!Number.isFinite(ordered) || ordered <= 0) return applyValue();

            const checks = [];
            if ((statusCheckWarns || statusCheckBlocks) && ordered > Number(currentRowData?.Available ?? 0)) {
                checks.push({
                    message: MESSAGES.SELECT_ITEMS_AVAILABLE_EXCEEDED,
                    title: MESSAGES.SELECT_ITEMS_AVAILABLE_TITLE,
                    blocks: statusCheckBlocks,
                });
            }
            if (netAvailChecked) {
                let message = null;
                if (ordered > Number(currentRowData?.NetAvailable ?? 0)) {
                    message = MESSAGES.SELECT_ITEMS_ITEM_NET_AVAIL_EXCEEDED;
                } else if (
                    manualLot &&
                    (currentRowData?.ItemType ?? "").trim() === ITEM_TYPE_LOT &&
                    ordered > Number(currentRowData?.LotNetAvailable ?? 0)
                ) {
                    message = MESSAGES.SELECT_ITEMS_LOT_NET_AVAIL_EXCEEDED;
                }
                if (message) {
                    checks.push({ message, title: MESSAGES.SELECT_ITEMS_NET_AVAIL_TITLE, blocks: netAvailBlocks });
                }
            }

            return (async () => {
                for (const check of checks) {
                    if (check.blocks) {
                        await alert(`${check.message} ${MESSAGES.SELECT_ITEMS_NET_AVAIL_BLOCKED}`, check.title);
                        return; // value never assigned, so the cell keeps what it had
                    }
                    const answer = await confirm(`${check.message} ${MESSAGES.SELECT_ITEMS_NET_AVAIL_CONTINUE}`, check.title);
                    if (answer !== true) return;
                }
                await applyValue();
            })();
        },
        [netAvailChecked, netAvailBlocks, statusCheckWarns, statusCheckBlocks, manualLot, rowKeyField, fetchRowPrice]
    );

    // A typed Price keeps Disc Price in step and marks the row as priced by the user.
    const setPriceCellValue = useCallback(
        (newData, value, currentRowData) => {
            newData.Price = value;
            newData.DiscPrice = discPriceOf(value, currentRowData?.DiscPercent);
            const key = currentRowData?.[rowKeyField];
            if (key != null) userPricedKeysRef.current.add(key);
        },
        [rowKeyField]
    );

    const setDiscPercentCellValue = useCallback((newData, value, currentRowData) => {
        newData.DiscPercent = value;
        newData.DiscPrice = discPriceOf(currentRowData?.Price, value);
    }, []);

    // Disc Price is Price less Disc %, so typing one is typing the other: the Disc % it implies is what the
    // append stores. (Legacy let this cell be edited and then ignored it - RunScript never reads DiscPrice.)
    // With no price to discount there is nothing to derive, and the cell keeps its derived value.
    const setDiscPriceCellValue = useCallback((newData, value, currentRowData) => {
        const price = Number(currentRowData?.Price);
        const discPrice = Number(value);
        if (value == null || value === "" || !Number.isFinite(discPrice) || !Number.isFinite(price) || price === 0) {
            newData.DiscPrice = discPriceOf(currentRowData?.Price, currentRowData?.DiscPercent);
            return;
        }
        newData.DiscPrice = discPrice;
        newData.DiscPercent = Math.round(((price - discPrice) / price) * 100 * 1e6) / 1e6;
    }, []);

    const handleHiding = useCallback(() => {
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    const handleAddSelected = useCallback(async () => {
        if (!headerRowId || headerRowId === emptyGuid) {
            alert(MESSAGES.SELECT_ITEMS_SAVE_ORDER_FIRST, MESSAGES.SELECT_ITEMS_TITLE);
            return;
        }
        const grid = dataGridRef.current?.instance();
        if (grid) grid.closeEditCell(); // flush the currently open editor into the changes set

        // editing.changes is the synchronous source of truth (incl. the cell just closed); fall back to state.
        const live = grid ? grid.option("editing.changes") || [] : [];
        const editedRows = live.length ? live : changes;
        const items = editedRows
            .filter((c) => c && c.key && c.data)
            .map((c) => {
                const ordered =
                    c.data.Ordered != null && c.data.Ordered !== "" ? Number(c.data.Ordered) : null;
                // The key is the item GUID, or "<item>|<lot>" in manual-lot mode. Only the key is available
                // for a row edited on a page the grid has since left, so both ids are read back out of it
                // rather than from the row data.
                const [keyItemId, keyLotId] = String(c.key ?? "").split("|");
                return {
                    itemId: keyItemId || null,
                    WarehouseID: warehouseId || null,
                    // Ignored server-side unless the profile is in manual-lot mode, where it is the lot the
                    // user picked; otherwise the server allocates lots after the insert.
                    lotId: manualLot && keyLotId ? keyLotId : null,
                    DetailType: "I",
                    quantityordered: ordered,
                    unitPrice: c.data.Price != null && c.data.Price !== "" ? Number(c.data.Price) : null,
                    DiscountPercent:
                        c.data.DiscPercent != null && c.data.DiscPercent !== "" ? Number(c.data.DiscPercent) : null,
                };
            })
            .filter((i) => i.quantityordered != null && i.quantityordered > 0);

        if (!items.length) {
            alert(MESSAGES.SELECT_ITEMS_ORDERED_REQUIRED, MESSAGES.SELECT_ITEMS_TITLE);
            return;
        }

        if (setLoadPanelVisible) setLoadPanelVisible(true);
        try {
            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.InsertTransactionDetails}`,
                { HeaderId: headerRowId, Items: items },
                {
                    headers: {
                        "Content-Type": "application/json",
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            if (res.data?.Success === false) {
                alert(res.data?.message || MESSAGES.SELECT_ITEMS_ADD_FAILED, MESSAGES.SELECT_ITEMS_ERROR_TITLE);
                return;
            }
            notify({ message: res.data?.message || MESSAGES.SELECT_ITEMS_ADDED, width: 300 }, "success", 3000);
            if (grid) grid.cancelEditData();
            if (onSuccess) onSuccess();
            handleHiding();
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            alert(typeof msg === "string" ? msg : JSON.stringify(msg), MESSAGES.SELECT_ITEMS_ERROR_TITLE);
        } finally {
            if (setLoadPanelVisible) setLoadPanelVisible(false);
        }
    }, [accessToken, headerRowId, warehouseId, onSuccess, handleHiding, setLoadPanelVisible, changes, manualLot]);

    // Ordered re-prices the row, and Price / Disc % / Disc Price keep one another in step (see their
    // setCellValue handlers); Amount is left to the server, which calculates it on append. The edited values
    // live in the grid's batch-edit changes, so they survive server-side paging and are read back on
    // "Add Selected Items".
    const qtyFormat = { type: "fixedPoint", precision: 2 };
    const moneyFormat = { type: "currency", precision: 2, currency: "USD", useGrouping: true };

    if (!visible) return null;

    return (
        <Popup
            visible={true}
            onHiding={handleHiding}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={MESSAGES.SELECT_ITEMS_LOTS_TITLE}
            container="#root"
            width="90vw"
            height="90vh"
        >
            <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>

                <DataGrid
                    ref={dataGridRef}
                    dataSource={resultData}
                    keyExpr={rowKeyField}
                    remoteOperations={true}
                    showBorders={true}
                    hoverStateEnabled={true}
                    rowAlternationEnabled={true}
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                    columnAutoWidth={true}
                    repaintChangesOnly={true}
                    height="calc(90vh - 190px)"
                    style={{ flex: "1 1 auto", minHeight: 0, marginTop: 8 }}
                    selectedRowKeys={selectedRowKeys}
                    onSelectionChanged={(e) => setSelectedRowKeys(e.selectedRowKeys)}
                >
                    <Selection mode="single" />
                    <Scrolling mode="standard" />
                    <LoadPanel enabled={true} />
                    <Sorting mode="multiple" />
                    <FilterRow visible={true} />
                    <Paging defaultPageSize={10} />
                    <Pager
                        visible={true}
                        showPageSizeSelector={true}
                        allowedPageSizes={allowedPageSizes}
                        showNavigationButtons={true}
                        showInfo={true}
                    />
                    <Editing
                        mode="batch"
                        allowUpdating={true}
                        useIcons={true}
                        selectTextOnEditStart={true}
                        changes={changes}
                        onChangesChange={setChanges}
                    />
                    <ColumnChooser enabled={true} title={MESSAGES.SELECT_ITEMS_COLUMN_CHOOSER} width={290} height={290} sortOrder="asc">
                        <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: MESSAGES.SELECT_ITEMS_COLUMN_SEARCH }} />
                    </ColumnChooser>

                    <Column dataField="RowId" caption={MESSAGES.SELECT_ITEMS_COL_ROW_ID} visible={false} allowEditing={false} />
                    <Column dataField="SelectionKey" visible={false} showInColumnChooser={false} allowEditing={false} />
                    <Column dataField="ItemType" visible={false} showInColumnChooser={false} allowEditing={false} />
                    <Column dataField="WarehouseId" caption={MESSAGES.SELECT_ITEMS_COL_WAREHOUSE} allowEditing={false} />
                    <Column dataField="ItemCode" caption={MESSAGES.SELECT_ITEMS_COL_ITEM} allowEditing={false} />
                    <Column dataField="Description1" caption={MESSAGES.SELECT_ITEMS_COL_ITEM_DESCRIPTION} allowEditing={false} />
                    {/* Deliberate deviation from legacy: uOMD0027.FormShow hides these four
                        (colLot/colLotDescription_1/colAvailable_Lot/colLotNetAvailable.Visible :=
                        fManualLotSelection), but here they stay on the grid in both lot modes so the
                        layout does not change shape when the profile does. They are read-only either way —
                        the lot is picked by choosing a row, never by typing — and in automatic-lot mode the
                        query selects them as NULL placeholders, so the cells simply come back empty.
                        Don't "restore" the legacy toggle: showing them always is the product decision. */}
                    <Column
                        dataField="LotCode"
                        caption={MESSAGES.SELECT_ITEMS_COL_LOT}
                        allowEditing={false}
                    />
                    <Column
                        dataField="LotDescription1"
                        caption={MESSAGES.SELECT_ITEMS_COL_LOT_DESCRIPTION}
                        allowEditing={false}
                    />
                    <Column
                        dataField="Available"
                        caption={MESSAGES.SELECT_ITEMS_COL_ITEM_AVAILABLE}
                        dataType="number"
                        allowEditing={false}
                        format={qtyFormat}
                    />
                    <Column
                        dataField="NetAvailable"
                        caption={MESSAGES.SELECT_ITEMS_COL_ITEM_NET_AVAILABLE}
                        dataType="number"
                        allowEditing={false}
                        format={qtyFormat}
                    />
                    <Column
                        dataField="LotAvailable"
                        caption={MESSAGES.SELECT_ITEMS_COL_LOT_AVAILABLE}
                        dataType="number"
                        allowEditing={false}
                        allowFiltering={false}
                        allowSorting={false}
                        format={qtyFormat}
                    />
                    <Column
                        dataField="LotNetAvailable"
                        caption={MESSAGES.SELECT_ITEMS_COL_LOT_NET_AVAILABLE}
                        dataType="number"
                        allowEditing={false}
                        allowFiltering={false}
                        allowSorting={false}
                        format={qtyFormat}
                    />
                    <Column
                        dataField="Ordered"
                        caption={MESSAGES.SELECT_ITEMS_COL_ORDERED}
                        dataType="number"
                        allowEditing={true}
                        setCellValue={setOrderedCellValue}
                        allowFiltering={false}
                        allowSorting={false}
                        format={qtyFormat}
                    />
                    <Column
                        dataField="Price"
                        caption={MESSAGES.SELECT_ITEMS_COL_PRICE}
                        dataType="number"
                        allowEditing={settings.allowPriceEdit}
                        setCellValue={setPriceCellValue}
                        allowFiltering={false}
                        allowSorting={false}
                        format={moneyFormat}
                    />
                    <Column
                        dataField="DiscPercent"
                        caption={MESSAGES.SELECT_ITEMS_COL_DISC_PERCENT}
                        dataType="number"
                        allowEditing={true}
                        setCellValue={setDiscPercentCellValue}
                        allowFiltering={false}
                        allowSorting={false}
                        format={qtyFormat}
                    />
                    <Column
                        dataField="DiscPrice"
                        caption={MESSAGES.SELECT_ITEMS_COL_DISC_PRICE}
                        dataType="number"
                        allowEditing={settings.allowPriceEdit}
                        setCellValue={setDiscPriceCellValue}
                        allowFiltering={false}
                        allowSorting={false}
                        format={moneyFormat}
                    />
                    <Column
                        dataField="Amount"
                        caption={MESSAGES.SELECT_ITEMS_COL_AMOUNT}
                        dataType="number"
                        allowEditing={false}
                        allowFiltering={false}
                        allowSorting={false}
                        format={moneyFormat}
                    />
                    <GridToolbar>
                        <GridToolbarItem location="after">
                            <Button
                                icon="trash"
                                disabled={!selectedRowKeys.length}
                                onClick={handleDeleteRows}
                            />
                        </GridToolbarItem>
                        <GridToolbarItem name="columnChooserButton" />
                    </GridToolbar>
                </DataGrid>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        paddingTop: 12,
                        borderTop: "1px solid #e0e0e0",
                        marginTop: 8,
                    }}
                >
                    <Button text={MESSAGES.SELECT_ITEMS_ADD} type="default" onClick={handleAddSelected} />
                    <Button text={MESSAGES.SELECT_ITEMS_CANCEL} onClick={handleHiding} />
                </div>
            </div>
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(SelectItemsPopup);
